import * as React from "react";

import { Link } from "react-router";

import { PlaybookSvg } from "@/components/pieces/icons/svg/playbook";
import { Avatar } from "@/components/pieces/users/Avatar";
import { MiniUserType } from "@/graphql";
import { cn } from "@/lib/utils";
import { SharePermissionState } from "@/types/permissions";

import { PermissionDropdown } from "./PermissionDropdown";
import { ShareGroup, ShareItemData, ShareProgram, ShareUser } from "./types";

function UserItemContent({
  user,
  isMe,
  isMarkedForRemoval,
}: {
  user: ShareUser;
  isMe: boolean;
  isMarkedForRemoval: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar user={user as MiniUserType} size={36} />
      <div className="flex flex-col gap-1">
        <div
          className={cn(
            "text-sm font-medium leading-none",
            isMarkedForRemoval && "line-through",
          )}
        >
          {user.name || user.email}
          {isMe && " (You)"}
        </div>
        {user.name && (
          <div className="text-xs text-muted-foreground leading-none">
            {user.email}
          </div>
        )}
      </div>
    </div>
  );
}

function GroupItemContent({
  group,
  isMarkedForRemoval,
}: {
  group: ShareGroup;
  isMarkedForRemoval: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-blue-200 flex items-center justify-center">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11 7C12.66 7 14 5.66 14 4C14 2.34 12.66 1 11 1C9.34 1 8 2.34 8 4C8 5.66 9.34 7 11 7ZM5 7C6.66 7 8 5.66 8 4C8 2.34 6.66 1 5 1C3.34 1 2 2.34 2 4C2 5.66 3.34 7 5 7ZM5 9C2.67 9 0 10.17 0 11.5V14H10V11.5C10 10.17 7.33 9 5 9ZM11 9C10.71 9 10.38 9.02 10.03 9.05C10.05 9.06 10.06 9.08 10.07 9.1C11.21 9.81 12 10.85 12 12.5V14H16V11.5C16 10.17 13.33 9 11 9Z"
            fill="currentColor"
            className="text-blue-900"
          />
        </svg>
      </div>
      <div className="flex flex-col gap-1">
        <div
          className={cn(
            "text-sm font-medium leading-none",
            isMarkedForRemoval && "line-through",
          )}
        >
          {group.name}
        </div>
        {group.workspaceName && (
          <div className="text-xs text-muted-foreground leading-none">
            Group Members
          </div>
        )}
      </div>
    </div>
  );
}

function ProgramItemContent({
  program,
  isMarkedForRemoval,
  subtitle,
}: {
  program: ShareProgram;
  isMarkedForRemoval: boolean;
  subtitle?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-blue-200 text-blue-900 flex items-center justify-center">
        <PlaybookSvg size={16} color="currentColor" />
      </div>
      <div className="flex flex-col gap-1">
        <Link
          to={`/programs/${program.id}`}
          className="no-underline hover:underline"
        >
          <div
            className={cn(
              "text-sm font-medium leading-none",
              isMarkedForRemoval && "line-through",
            )}
          >
            {program.name}
          </div>
        </Link>
        {(subtitle || program.subtitle) && (
          <div className="text-xs text-muted-foreground leading-none">
            {subtitle || program.subtitle}
          </div>
        )}
      </div>
    </div>
  );
}

export function ShareItem({
  data,
  onUpdateShareStatus,
  className,
}: {
  data: ShareItemData;
  onUpdateShareStatus?: (status: SharePermissionState) => void;
  className?: string;
}) {
  const [isMarkedForRemoval, setIsMarkedForRemoval] = React.useState(
    data.shareStatus === "REMOVE",
  );

  React.useEffect(() => {
    setIsMarkedForRemoval(data.shareStatus === "REMOVE");
  }, [data.shareStatus]);

  const handleStatusChange = (status: SharePermissionState) => {
    if (status === "REMOVE") {
      setIsMarkedForRemoval(true);
    }
    onUpdateShareStatus?.(status);
  };

  const renderContent = () => {
    switch (data.type) {
      case "user":
        return (
          <UserItemContent
            user={data.item as ShareUser}
            isMe={data.isMe || false}
            isMarkedForRemoval={isMarkedForRemoval}
          />
        );
      case "group":
        return (
          <GroupItemContent
            group={data.item as ShareGroup}
            isMarkedForRemoval={isMarkedForRemoval}
          />
        );
      case "program":
        return (
          <ProgramItemContent
            program={data.item as ShareProgram}
            isMarkedForRemoval={isMarkedForRemoval}
            subtitle={(data.item as ShareProgram).subtitle}
          />
        );
    }
  };

  const showPermissionControl =
    data.canModifyPermissions !== false && !data.isOwner && onUpdateShareStatus;

  return (
    <div className={cn("flex items-center justify-between py-3", className)}>
      {renderContent()}
      <div className="text-sm font-medium">
        {data.isOwner ? (
          "Owner"
        ) : showPermissionControl ? (
          <PermissionDropdown
            value={data.shareStatus}
            onChange={handleStatusChange}
            availablePermissions={data.availablePermissions}
          />
        ) : (
          <span className="text-muted-foreground">
            {data.shareStatus === "VIEW"
              ? "Viewer"
              : data.shareStatus === "EDIT"
                ? "Editor"
                : data.shareStatus === "SHARE"
                  ? "Can share"
                  : "Viewer"}
          </span>
        )}
      </div>
    </div>
  );
}
